import _ from 'underscore';
import React from 'react';
import { ActivitySpinnerWidget, TextFieldWidget } from '@tableau/widgets';
import { AddUsersToGroupDataKeys, AddUsersToGroupGrid } from './AddUsersToGroupGrid';
import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { Messages } from '../../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { ResourceQuery } from '../../../ts/resources/ResourceQuery';
import { Set } from '../../../ts/models/Set';
import { SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { SiteRolePickerDropdownWithCount } from '../../components/SiteRolePickerDropdownWithCount';
import { siteRolePickerTopMargin, textFieldStyles } from './styles/AddUsersToGroupDialogStyles';


export const INITIAL_SLICE_SIZE = 100;
export const SCROLL_SLICE_SIZE = 50;

export namespace AddUsersToGroupDialogTestIds {
    export const siteRoleDropdownTestId = 'add-users-site-role-dropdown';
    export const searchFieldTestId = 'add-users-username-filter';
    export const activitySpinnerTestId = 'add-users-activity-spinner';
    export const noResultsTestId = 'add-users-no-results';
    export const labelTestId = 'add-users-label-';
    export const errorTestId = 'add-users-error-';
}

export type AddUsersToGroupDialogBodyProps = {
    readonly group: IGroupInfo;
    getQuery: (siteRole: string, searchString: string, resultOrder: IOrderClause<string>) => ResourceQuery<ISiteUserInfo>;
    inGroupUsersList: ISiteUserInfo[];
};

export type AddUsersToGroupDialogState = {
  addableResultsList: ISiteUserInfo[];
  selectedUserIds: string[];
  selectedIUserInfoSet: Set<IUserInfo>;
};

export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<AddUsersToGroupDialogBodyProps, AddUsersToGroupDialogState>;

export function calculateAddableResultsList(resultsList: ISiteUserInfo[], inGroupList: ISiteUserInfo[]) {
  // return list of resultsList minus items in inGroupList
  let addableUsers: ISiteUserInfo[] = [];
  resultsList.forEach(userFromResults => {
    if (inGroupList.findIndex(userFromGroup => userFromResults.id === userFromGroup.id) === -1) {
      addableUsers.push(userFromResults);
    }
  });
  return addableUsers;
}

export function calculateRowCheckboxesState(rowCheckboxesState: _.Dictionary<string>, addableUsers: ISiteUserInfo[]): _.Dictionary<string> {
  // return rowCheckboxesState with only elements in addableUsers
  let newRowCheckboxesState: _.Dictionary<string> = {};
  addableUsers.forEach((user: ISiteUserInfo) => {
    if (rowCheckboxesState[user.id] === CheckboxState.checked) {
      newRowCheckboxesState[user.id] = CheckboxState.checked;
    }
  });
  return newRowCheckboxesState;
}

export function calculateIUserInfoSet(rowCheckboxesState: _.Dictionary<string>, addableUsers: ISiteUserInfo[]): Set<IUserInfo> {
  let userSet: Set<IUserInfo> = new Set((item) => item.id);
  addableUsers.forEach((user: ISiteUserInfo) => {
    if (rowCheckboxesState[user.id] === CheckboxState.checked) {
      userSet.add(user as IUserInfo);
    }
  });
  return userSet;
}

export function getFieldFromISiteUserInfo(user: ISiteUserInfo, field: string) {
  switch (field) {
    case AddUsersToGroupDataKeys.SiteUserDisplayName:
      return user.displayName;
    case AddUsersToGroupDataKeys.SiteUserName:
      return user.username;
    case AddUsersToGroupDataKeys.SiteUserRole:
      return user.siteRole;
    case AddUsersToGroupDataKeys.SiteUserLastSignIn:
      return user.lastSignIn ?? '';
    default:
      throw new Error('Cannot sort ISiteUserInfo by ' + field);
  }
}

export function createCompareFunctionFromColumnSortOrder(columnSortOrder: IOrderClause):
(arg0: ISiteUserInfo, arg1: ISiteUserInfo) => number {
  // return a negative value if first argument is less than second argument,
  // zero if they're equal and a positive value otherwise
  return (arg0: ISiteUserInfo, arg1: ISiteUserInfo) => {
    const field0 = getFieldFromISiteUserInfo(arg0, columnSortOrder.field).toLowerCase();
    const field1 = getFieldFromISiteUserInfo(arg1, columnSortOrder.field).toLowerCase();
    if (field0 === field1) {
      return 0;
    }
    else if (field0 < field1) {
      return columnSortOrder.ascending ? -1 : 1;
    }
    else {
      return columnSortOrder.ascending ? 1 : -1;
    }
  };
}

function sortSearchResultsByColumnSortOrder(searchResults: ISiteUserInfo[], columnSortOrder: IOrderClause): ISiteUserInfo[] {
  return searchResults.sort(createCompareFunctionFromColumnSortOrder(columnSortOrder));
}

function spinnerChild(): JSX.Element {
  return (
    <div className={'tb-data-grid-empty'}>
      <ActivitySpinnerWidget testId={AddUsersToGroupDialogTestIds.activitySpinnerTestId} shouldShowUnderlay={false}/>
    </div>
  );
}

export function emptyChild(): JSX.Element{
  return (
    <div className={'tb-data-grid-empty'}>
      <div className={'tb-data-grid-empty-title'} data-tb-test-id={AddUsersToGroupDialogTestIds.noResultsTestId}>
        {Messages.noMatchingResults()}
      </div>
    </div>
  );
}

function errorChild(): JSX.Element{
  return (
    <div className={'tb-data-grid-error'}>
      <div className={'tb-data-grid-error-title'} data-tb-test-id={AddUsersToGroupDialogTestIds.errorTestId}>
        {Messages.errorUnknown()}
      </div>
    </div>
  );
}


export function AddUsersToGroupDialogBody(props: PropsForConfirmActionDialogBody): JSX.Element {
    const siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;

    const [columnSortOrder, setColumnSortOrder] = React.useState({
      field: AddUsersToGroupDataKeys.SiteUserDisplayName,
      ascending: true
    } as IOrderClause);
    const [rowCheckboxesState, setRowCheckboxesState] = React.useState({} as _.Dictionary<string>); // maps user id to CheckboxState
    const [searchText, setSearchText] = React.useState(''); // the contents of the search field that the user can set
    const [siteRoleFilter, setSiteRoleFilter] = React.useState(siteRolesOptionResource.getAnySiteRole().role); // the status of the site role filter that users can set
    const [searchResults, setSearchResults] = React.useState([] as ISiteUserInfo[]); // the results that are displayed in the grid
    const [isError, setIsError] = React.useState(false); // flag to track error state
    const [doneInitialFetching, setDoneInitialFetching] = React.useState(false); // flag to track when initial fetching is done
    const [getNextPage, setGetNextPage] = React.useState(false); // flag that is true when scroll in datagrid bottoms out and needs more data
    const [remoteResultCount, setRemoteResultCount] = React.useState(0); // total count ON THE SERVER of results of a particular filtering of users
    const [localResultCount, setLocalResultCount] = React.useState(0); // count ON THE CLIENT of downloaded results
    const [allowSliceFetchLock, setAllowSliceFetchLock] = React.useState(true); // lock claimed when false, so slice doesn't concurrently refetch

    const fetchFirstChunkOfUsers = async () => {
      // fetch first bunch
      const usersResult: ListResult<ISiteUserInfo> = await props.getQuery(siteRoleFilter, searchText, columnSortOrder)
        .getSlice(0, INITIAL_SLICE_SIZE);
      setSearchResults(_ => usersResult.items);
      // calculate total number and save it
      setRemoteResultCount(_ => usersResult.total);
      // calculate local result size and save it
      setLocalResultCount(_ => usersResult.items.length);
      // when done running this hook, set done fetching to true
      setDoneInitialFetching(_ => true);
    };

    const fetchNextChunkOfUsers = async () => {
      // fetch next bunch
      const usersResult: ListResult<ISiteUserInfo> = await props.getQuery(siteRoleFilter, searchText, columnSortOrder)
        .getSlice(localResultCount, SCROLL_SLICE_SIZE);
      // calculate local result size and save it
      setLocalResultCount(previousValue => previousValue + usersResult.items.length);
      // concatenate fetched results to internal list
      setSearchResults(previousResults =>  previousResults.concat(usersResult.items));
      // drop the getNextPage trigger
      setGetNextPage(_ => false);
      // release slice fetch lock
      setAllowSliceFetchLock(_ => true);
    };

    React.useEffect(() => {
      // set done fetching to false
      setDoneInitialFetching(_ => false);
      try {
        fetchFirstChunkOfUsers();
      } catch(e) {
        // if error, set isError
        setIsError(_ => true);
      }
    }, [searchText, siteRoleFilter, columnSortOrder]); // list search params here at the end

    React.useEffect(() => {
      //  fetch if slicelock is released and there are more results to grab
      if (remoteResultCount !== localResultCount && allowSliceFetchLock) {
        try {
          setAllowSliceFetchLock(_ => false);
          fetchNextChunkOfUsers();
        } catch(e) {
          // if error, set isError
          setIsError(_ => true);
        }
      }
    }, [getNextPage]);

    return <>
        <div data-tb-test-id={AddUsersToGroupDialogTestIds.labelTestId}>
          {Messages.addUsersToGroupDescription({GROUP_NAME: props.group.name})}
        </div>
        <div className={siteRolePickerTopMargin}>
          <SiteRolePickerDropdownWithCount
              testId={AddUsersToGroupDialogTestIds.siteRoleDropdownTestId}
              includeAnySiteRoleOption={true}
              setFilterValue={setSiteRoleFilter}
          />
        </div>
        <TextFieldWidget
            testId={AddUsersToGroupDialogTestIds.searchFieldTestId}
            placeholder={Messages.searchForAUserOrUsername()}
            kind={'search'}
            label={null}
            className={textFieldStyles}
            style={{width:'100%'}}
            onChange={(e) => {
                setSearchText(e.target.value);
            }}
            message={'\u200B'}
        />
        <AddUsersToGroupGrid
          emptyChild={isError? errorChild() : doneInitialFetching ? emptyChild() : spinnerChild()}
          userList={!isError && doneInitialFetching ? sortSearchResultsByColumnSortOrder(searchResults, columnSortOrder) : []}
          inGroupUsersList={props.inGroupUsersList}
          currentColumnSortOrder={columnSortOrder}
          handleColumnSortOrderChange={handleColumnSortOrderChange}
          rowCheckboxState={
            // A dictionary mapping ISiteUserInfo.id (a string) to "checked" if the user is selected
            rowCheckboxesState
          }
          handleRowCheckboxChange={handleRowCheckboxChange}
          shouldAskForNextPage={(dataGridEndIndex: number): boolean => {
            // AddUsersToGroupGrid will ask for another page if all the following conditions are true
            //   1. reach end of scroll in grid (dataGridEndIndex + 1 === localCount)
            //   2. there is more data to fetch from server (localCount < remoteCount)
            return dataGridEndIndex + 1 === localResultCount && localResultCount < remoteResultCount ;
          }}
          askForNextPage={() => {
            setGetNextPage(_ => true);
          }}
      />
  </>;

  function handleColumnSortOrderChange(field: string): void {
    let newColumnSortOrder: IOrderClause = undefined;
    newColumnSortOrder = {
      field: field as AddUsersToGroupDataKeys,
      ascending: field === columnSortOrder.field ? !columnSortOrder.ascending : true
    };
    setColumnSortOrder(_ => {
      return newColumnSortOrder;
    });
  }

  function handleRowCheckboxChange(rowUser: ISiteUserInfo): void {
    const checkboxUserId = rowUser.id;
    const thisRowCheckboxState = rowCheckboxesState[checkboxUserId];
    const addableUsers = calculateAddableResultsList(searchResults, props.inGroupUsersList);
    let newRowCheckboxesState = rowCheckboxesState;
    if (thisRowCheckboxState === CheckboxState.checked) {
      delete newRowCheckboxesState[checkboxUserId];
    } else {
      newRowCheckboxesState[checkboxUserId] = CheckboxState.checked;
    }
    setRowCheckboxesState(_ => newRowCheckboxesState);
    // set header checkbox state
    props.updateDialogState(previousState => {
      return {
        ...previousState,
        selectedUserIds: Object.keys(newRowCheckboxesState),
        selectedIUserInfoSet: calculateIUserInfoSet(newRowCheckboxesState, addableUsers)
      };
    });
  }
}